import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  LinearProgress,
  Button
} from '@mui/material';
import withoutQR from "../../../assests/updatedQr.png";
import jsPDF from 'jspdf';
import centerqrlogo from "../../../assests/grouplogo1.svg";
import { FaDownload, FaSpinner, FaTimes } from 'react-icons/fa';
import './BulkQRCodeGenerator.css';

const BulkQRCodeGenerator = ({ open, onClose, uids, onPDFDownloadComplete, pdfDownloading }) => {
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [qrCodesLoaded, setQrCodesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState(0);
  const [qrCodeImages, setQrCodeImages] = useState({});
  const containerRef = useRef(null);
  const autoTriggeredRef = useRef(false);

  // Card dimensions for A4 page (4 per page)
  const CARD_WIDTH = 95;
  const CARD_HEIGHT = 140;
  const CARDS_PER_PAGE = 4;

  const CARD_WIDTH_PX = 378;
  const CARD_HEIGHT_PX = 600;

  const generateQRCodeUrl = (uid) => {
    const qrData = `https://www.daalchini.co.in/uuid?value=${uid}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}&format=png&margin=10`;
  };

  useEffect(() => {
    if (open && uids.length > 0) {
      autoTriggeredRef.current = false;
      setQrCodesLoaded(false);
      setLoadedImages(0);
      setQrCodeImages({});
      preloadAllImages();
    }
  }, [open, uids]);

  useEffect(() => {
    if (open && Array.isArray(uids) && uids.length > 0 && qrCodesLoaded && !isDownloading && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true;
      const timer = setTimeout(() => {
        downloadOptimizedPDF();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open, uids, qrCodesLoaded, isDownloading]);

  const preloadAllImages = async () => {
    try {
      const imagesMap = {};

      for (const uidObj of uids) {
        const uid = uidObj && uidObj.uid ? uidObj.uid : uidObj;

        try {
          const qrImageBlob = await loadImageAsBlob(generateQRCodeUrl(uid));
          const bgImageBlob = await loadImageAsBlob(withoutQR);
          const logoImageBlob = await loadImageAsBlob(centerqrlogo);

          imagesMap[uid] = {
            uid,
            qrImage: URL.createObjectURL(qrImageBlob),
            bgImage: URL.createObjectURL(bgImageBlob),
            logoImage: URL.createObjectURL(logoImageBlob)
          };
        } catch (error) {
          console.error(`Failed to load images for UID ${uid}:`, error);
          imagesMap[uid] = {
            uid,
            qrImage: generateQRCodeUrl(uid),
            bgImage: withoutQR,
            logoImage: centerqrlogo
          };
        }
      }

      setQrCodeImages(imagesMap);
      setQrCodesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      setQrCodesLoaded(true);
    }
  };

  const loadImageAsBlob = (url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(new Error(`Failed to load image: ${url}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Network error loading image: ${url}`));
      xhr.send();
    });
  };


  const createCardCanvas = async (uid, qrImageUrl, bgImageUrl, logoImageUrl) => {
    return new Promise(async (resolve) => {
      const canvas = document.createElement('canvas');
      const scale = 3;
      canvas.width = CARD_WIDTH_PX * scale;
      canvas.height = CARD_HEIGHT_PX * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      try {
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        
        bgImg.onload = () => {
          ctx.drawImage(bgImg, 0, 0, CARD_WIDTH_PX, CARD_HEIGHT_PX);

          const qrImg = new Image();
          qrImg.crossOrigin = "anonymous";
          
          qrImg.onload = () => {
            const qrSize = 240;
            const qrX = (CARD_WIDTH_PX - qrSize) / 2;
            const qrY = (CARD_HEIGHT_PX - qrSize) / 2 - 90;

            ctx.fillStyle = 'transparent';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 6;
            ctx.fillRect(qrX - 20, qrY - 20, qrSize + 35, qrSize + 35);
            
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

            const logoImg = new Image();
            logoImg.crossOrigin = "anonymous";
            
            logoImg.onload = () => {
              const logoSize = 40;
              const logoX = qrX + (qrSize - logoSize) / 2;
              const logoY = qrY + (qrSize - logoSize) / 2;
              
              ctx.fillStyle = 'transparent';
              ctx.beginPath();
              ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2 + 2, 0, 2 * Math.PI);
              ctx.fill();
              
              ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

             
              const uidText = `${uid}`;
              ctx.font = 'bold 20px Arial';
              ctx.fillStyle = '#1e293b';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              const textX = CARD_WIDTH_PX / 2 + 22;
              const textY = qrY + qrSize + 24;
              ctx.fillText(uidText, textX, textY);

              resolve(canvas);
            };

            logoImg.onerror = () => {
              // FALLBACK
              const uidText = `${uid}`;
              ctx.font = 'bold 20px Arial';
              ctx.fillStyle = '#1e293b';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              const textX = CARD_WIDTH_PX / 2 + 20;
              const textY = qrY + qrSize + 20;
              ctx.fillText(uidText, textX, textY);
              
              resolve(canvas);
            };

            logoImg.src = logoImageUrl;
          };

          qrImg.onerror = () => {
            console.error('QR code failed to load for UID:', uid);
            resolve(canvas);
          };

          qrImg.src = qrImageUrl;
        };

        bgImg.onerror = () => {
          console.error('Background image failed to load');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, CARD_WIDTH_PX, CARD_HEIGHT_PX);
          
          ctx.font = 'bold 24px Arial';
          ctx.fillStyle = '#000000';
          ctx.textAlign = 'center';
          ctx.fillText('Daalchini', CARD_WIDTH_PX / 2, CARD_HEIGHT_PX / 2);
          
          resolve(canvas);
        };

        bgImg.src = bgImageUrl;
      } catch (error) {
        console.error('Error creating card canvas:', error);
        resolve(canvas);
      }
    });
  };

  const downloadOptimizedPDF = async () => {
    if (!Array.isArray(uids) || uids.length === 0 || isDownloading) return;

    const totalQrCodes = uids.length;
    const totalPages = Math.ceil(totalQrCodes / CARDS_PER_PAGE);

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const pdf = new jsPDF({ 
        orientation: 'portrait', 
        unit: 'mm', 
        format: 'a4' 
      });

      console.log(`Generating single PDF with ${totalQrCodes} QR codes across ${totalPages} pages`);

      let processedCards = 0;

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const startIndex = page * CARDS_PER_PAGE;
        const endIndex = Math.min(startIndex + CARDS_PER_PAGE, totalQrCodes);
        const pageCards = uids.slice(startIndex, endIndex);

        for (let cardIndex = 0; cardIndex < pageCards.length; cardIndex++) {
          const uidObj = pageCards[cardIndex];
          const uid = uidObj && uidObj.uid ? uidObj.uid : uidObj;

          if (!uid && uid !== 0) {
            continue;
          }

          const images = qrCodeImages[uid] || {
            qrImage: generateQRCodeUrl(uid),
            bgImage: withoutQR,
            logoImage: centerqrlogo
          };

          const canvas = await createCardCanvas(uid, images.qrImage, images.bgImage, images.logoImage);

          const row = Math.floor(cardIndex / 2);
          const col = cardIndex % 2;

          const pageWidth = 210;
          const pageHeight = 297;

          const horizontalMargin = (pageWidth - (2 * CARD_WIDTH)) / 3;
          const verticalMargin = (pageHeight - (2 * CARD_HEIGHT)) / 3;

          const x = col * (CARD_WIDTH + horizontalMargin) + horizontalMargin;
          const y = row * (CARD_HEIGHT + verticalMargin) + verticalMargin;

          const imgData = canvas.toDataURL('image/png', 1.0);
          pdf.addImage(imgData, 'PNG', x, y, CARD_WIDTH, CARD_HEIGHT);

          processedCards += 1;
          const progress = Math.round((processedCards / totalQrCodes) * 100);
          setDownloadProgress(progress);
        }

        await new Promise(resolve => setTimeout(resolve, 150));
      }

      const pdfFileName = `daalchini_qr_cards_${totalQrCodes}_uids_${new Date().toISOString().split('T')[0]}.pdf`;
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = pdfFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      
      console.log(`Single PDF created successfully with ${totalQrCodes} QR codes: ${pdfFileName}`);

    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      if (onPDFDownloadComplete) onPDFDownloadComplete();
    }
  };

  const handleClose = () => {
    if (!isDownloading) onClose();
  };

  useEffect(() => {
    return () => {
      Object.values(qrCodeImages).forEach(imageObj => {
        if (imageObj.qrImage && imageObj.qrImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.qrImage);
        }
        if (imageObj.bgImage && imageObj.bgImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.bgImage);
        }
        if (imageObj.logoImage && imageObj.logoImage.startsWith('blob:')) {
          URL.revokeObjectURL(imageObj.logoImage);
        }
      });
    };
  }, [qrCodeImages]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{ 
        style: { 
          borderRadius: '20px', 
          overflow: 'hidden', 
          minHeight: '500px', 
          maxWidth: '1200px',
          position: 'relative'
        } 
      }}
    >
      <button 
        className="bqr-close-btn"
        onClick={handleClose}
        disabled={isDownloading}
      >
        <FaTimes />
      </button>

      <DialogContent sx={{ p: 3 }}>
        <Box className="bqr-container" ref={containerRef}>
          <Box className="bqr-header">
            <Typography variant="h5" className="bqr-title">
              {isDownloading ? `Generating PDF... ${downloadProgress}%` : `QR Codes for ${uids.length} UIDs`}
            </Typography>
            <Typography variant="body2" className="bqr-subtitle">
              {Math.ceil(uids.length / 4)} A4 pages • {uids.length} QR codes
            </Typography>
          </Box>

          {isDownloading && (
            <Box className="bqr-progress-section">
              <Typography variant="body2" className="bqr-progress-text">
                Creating PDF with {uids.length} QR codes... {downloadProgress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={downloadProgress} 
                className="bqr-progress-bar"
              />
              <Typography variant="body2" className="bqr-progress-note">
                Organizing {uids.length} cards into A4 pages (4 per page)...
              </Typography>
            </Box>
          )}

          {!qrCodesLoaded && !isDownloading && (
            <Box className="bqr-loading-section">
              <FaSpinner className="bqr-spinner" />
              <Typography variant="body1" className="bqr-loading-text">
                Loading QR codes... {Object.keys(qrCodeImages).length}/{uids.length} loaded
              </Typography>
              <LinearProgress className="bqr-loading-bar" />
            </Box>
          )}

          {!isDownloading && qrCodesLoaded && (
            <Box className="bqr-grid">
              {uids.map((uidObj, index) => {
                const uid = uidObj.uid || uidObj;
                const images = qrCodeImages[uid] || {
                  qrImage: generateQRCodeUrl(uid),
                  bgImage: withoutQR,
                  logoImage: centerqrlogo
                };

                return (
                  <Box 
                    key={`${uid}-${index}`}
                    className="bqr-card"
                  >
                    <Box className="bqr-card-inner">
                      <img 
                        src={images.bgImage} 
                        alt="Poster Background" 
                        className="bqr-card-bg"
                        crossOrigin="anonymous"
                      />
                      
                      <Box className="bqr-qr-wrapper">
                        <img 
                          src={images.qrImage} 
                          alt={`QR Code for ${uid}`}
                          className="bqr-qr-image"
                          crossOrigin="anonymous"
                        />
                        <Box className="bqr-logo-wrapper">
                          <img 
                            src={images.logoImage} 
                            alt="Logo"
                            className="bqr-logo-image"
                            crossOrigin="anonymous"
                          />
                        </Box>
                      </Box>

                      <Box className="bqr-uid-wrapper">
                        <Typography className="bqr-uid-text">
                          {/* 🔥 Sirf UID value - Image mein "UID : " already hai */}
                          {uid}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkQRCodeGenerator;
